import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { api, session, setUnauthenticatedHandler, type CurrentUser } from './api';

interface AuthState {
  user: CurrentUser | null;
  loading: boolean;
  /** Empresa e filial ativas. Todo o ERP opera sob um escopo (§2). */
  companyId: string | null;
  branchId: string | null;
  setScope: (companyId: string, branchId: string | null) => void;
  login: (tenant: string, email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  /** Verifica permissao; administrador do tenant passa por tudo (§4). */
  can: (...permissions: string[]) => boolean;
}

const AuthContext = createContext<AuthState | null>(null);

const SCOPE_KEY = 'dwu.scope';

function readScope(): { companyId: string | null; branchId: string | null } {
  try {
    const raw = window.localStorage.getItem(SCOPE_KEY);
    if (!raw) return { companyId: null, branchId: null };
    return JSON.parse(raw) as { companyId: string | null; branchId: string | null };
  } catch {
    return { companyId: null, branchId: null };
  }
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CurrentUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [branchId, setBranchId] = useState<string | null>(null);

  // Sessao expirada em qualquer chamada leva ao login, sem deixar a tela num
  // estado meio carregado.
  useEffect(() => {
    setUnauthenticatedHandler(() => {
      setUser(null);
      window.location.assign('/login');
    });
  }, []);

  const loadUser = useCallback(async () => {
    if (!session.accessToken) {
      setLoading(false);
      return;
    }

    try {
      const current = await api.get<CurrentUser>('/auth/me');
      setUser(current);

      const stored = readScope();
      const preferred =
        current.companyAccess.find((access) => access.company.id === stored.companyId) ??
        current.companyAccess.find((access) => access.isDefault) ??
        current.companyAccess[0];

      if (preferred) {
        setCompanyId(preferred.company.id);
        setBranchId(stored.companyId === preferred.company.id ? stored.branchId : (preferred.branch?.id ?? null));
      }
    } catch {
      session.clear();
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadUser();
  }, [loadUser]);

  const setScope = useCallback((nextCompany: string, nextBranch: string | null) => {
    setCompanyId(nextCompany);
    setBranchId(nextBranch);
    try {
      window.localStorage.setItem(
        SCOPE_KEY,
        JSON.stringify({ companyId: nextCompany, branchId: nextBranch }),
      );
    } catch {
      /* preferencia apenas nesta sessao */
    }
  }, []);

  const login = useCallback(
    async (tenant: string, email: string, password: string) => {
      const result = await api.public.post<{
        accessToken: string;
        refreshToken: string;
        user: { tenantSlug: string };
      }>('/auth/login', { tenant, email, password });

      session.save(result, result.user.tenantSlug);

      // Recarga completa em vez de navegacao do roteador: a troca de identidade
      // precisa descartar todo o estado em memoria — permissoes, listas em cache,
      // filtros da tela anterior. Navegar preservaria esse estado, e o operador
      // veria dados do usuario que acabou de sair.
      window.location.assign('/');
    },
    [],
  );

  const logout = useCallback(async () => {
    const refreshToken = session.refreshToken;
    // Revoga no servidor antes de limpar: sem isso o refresh continuaria valido.
    if (refreshToken) {
      await api.public.post('/auth/logout', { refreshToken }).catch(() => undefined);
    }
    session.clear();
    setUser(null);
    window.location.assign('/login');
  }, []);

  const can = useCallback(
    (...permissions: string[]) => {
      if (!user) return false;
      if (user.isTenantAdmin) return true;
      return permissions.every((permission) => user.permissions.includes(permission));
    },
    [user],
  );

  const value = useMemo<AuthState>(
    () => ({ user, loading, companyId, branchId, setScope, login, logout, can }),
    [user, loading, companyId, branchId, setScope, login, logout, can],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth precisa estar dentro de AuthProvider.');
  return context;
}
